/**
 * DeckCardDetailsPanel - Slide-out card details panel for deck editor
 *
 * Shows comprehensive card information when a card is selected in the deck editor.
 * Reuses card display components from collection for consistent styling.
 * Supports two modes:
 * - Preview: Viewing a card from search results (can add to deck)
 * - In-Deck: Viewing a card already in the deck (can edit quantity/zone)
 */
import clsx from 'clsx';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import type { ReactElement } from 'react';
import { useCallback, useEffect, useMemo } from 'react';
import { IoAddCircleOutline, IoClose, IoOpenOutline } from 'react-icons/io5';

import CardShowcase from '@/components/CardDetails/CardShowcase';
import CardShowcaseSkeleton from '@/components/CardDetails/CardShowcaseSkeleton';
import LegalitiesSection from '@/components/CardDetails/LegalitiesSection';
import PricesSection from '@/components/CardDetails/PricesSection';
import PricesSectionSkeleton from '@/components/CardDetails/PricesSectionSkeleton';
import QuickActionsBar from '@/components/CardDetails/QuickActionsBar';
import RulingsSection from '@/components/CardDetails/RulingsSection';
import RulingsSectionSkeleton from '@/components/CardDetails/RulingsSectionSkeleton';
import { closeCardDetailsAtom, deckCardDetailsAtom } from '@/features/decks/state/deckCardDetailsAtom';
import { cardIndexByIdAtom } from '@/services/cardDatabase';
import type { ScryfallCard } from '@/types/card';
import type { DeckCard } from '@/types/deck';
import { activeDeckAtom } from '../../state/decksAtom';

import CardAvailabilitySection from '@/components/CardDetails/CardAvailabilitySection';
import { getOracleId } from '@/lib/cardUtils';
import InDeckSection from './InDeckSection';
import InDeckSectionSkeleton from './InDeckSectionSkeleton';
import VersionSection from './VersionSection';

// ============================================================================
// Types
// ============================================================================

export interface DeckCardDetailsPanelProps {
  /** Whether the panel is open */
  isOpen: boolean;
  /** Scryfall card data (from card cache) */
  scryfallCard?: ScryfallCard | null;
  /** Whether Scryfall data is loading */
  isLoadingScryfall?: boolean;
  /** Callback to add card to deck (in preview mode) */
  onAddToDeck?: (scryfallId: string) => void;
  /** Additional CSS classes */
  className?: string;
}

// ============================================================================
// Component
// ============================================================================

export default function DeckCardDetailsPanel({
  isOpen,
  scryfallCard,
  isLoadingScryfall = false,
  onAddToDeck,
  className,
}: DeckCardDetailsPanelProps): ReactElement | null {
  const [detailsState] = useAtom(deckCardDetailsAtom);
  const closePanel = useSetAtom(closeCardDetailsAtom);
  const activeDeck = useAtomValue(activeDeckAtom);
  const cardsById = useAtomValue(cardIndexByIdAtom);

  const { scryfallId, mode, deckCardId } = detailsState;

  // Get minimal card data for quick display (before Scryfall loads)
  const minimalCard = scryfallId ? cardsById.get(scryfallId) : null;

  // Find the deck card if in in-deck mode
  const deckCard: DeckCard | null = useMemo(() => {
    if (mode !== 'in-deck' || !deckCardId || !activeDeck.cards) return null;
    return activeDeck.cards.find((c) => c.id === deckCardId) ?? null;
  }, [mode, deckCardId, activeDeck.cards]);

  // Card name (from Scryfall > minimal > deck card)
  const cardName = scryfallCard?.name ?? minimalCard?.name ?? deckCard?.card_name ?? 'Loading...';

  // Oracle ID for list integration and quick actions
  const oracleId = getOracleId(scryfallCard) ?? minimalCard?.oracle_id ?? null;

  // log all relevant data for debugging
  useEffect(() => {
    console.log('DeckCardDetailsPanel data:', {
      scryfallId,
      mode,
      deckCardId,
      cardName,
      oracleId,
      scryfallCard,
      minimalCard,
      deckCard,
    });
  }, [scryfallId, mode, deckCardId, cardName, oracleId, scryfallCard, minimalCard, deckCard]);

  // Keyboard handler for Escape
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        closePanel();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, closePanel]);

  // Handle add to deck
  const handleAddToDeck = useCallback(() => {
    if (scryfallId && onAddToDeck) {
      onAddToDeck(scryfallId);
    }
  }, [scryfallId, onAddToDeck]);

  if (!isOpen || !scryfallId) return null;

  const isInDeck = mode === 'in-deck' && deckCard !== null;

  return (
    <aside className={clsx('bg-obsidian/95 border-storm flex h-full w-80 flex-col border-l backdrop-blur-sm xl:w-96', className)}>
      {/* Header */}
      <header className="border-storm flex items-center justify-between border-b px-4 py-3">
        <div className="min-w-0 flex-1">
          <h2 className="text-pearl truncate text-sm font-medium">{cardName}</h2>
          <p className="text-ash text-xs">{isInDeck ? 'In this deck' : 'Preview'}</p>
        </div>
        <div className="flex items-center gap-1">
          {/* Close button */}
          <button
            type="button"
            onClick={closePanel}
            className="text-ash hover:text-pearl rounded p-1 transition-colors"
            aria-label="Close panel"
          >
            <IoClose className="h-5 w-5" />
          </button>
        </div>
      </header>

      {/* Quick Actions Bar - Star, List, Collection (hide Deck since we're in deck editor) */}
      {oracleId && (
        <QuickActionsBar
          oracleId={oracleId}
          cardName={cardName}
          card={scryfallCard ?? null}
          scryfallId={scryfallId ?? undefined}
          hideDeck
        />
      )}

      {/* Scrollable content */}
      <div className="flex-1 overflow-y-auto">
        {/* Card Showcase (Hero) - Show skeleton during loading, component when ready */}
        {scryfallCard ? <CardShowcase card={scryfallCard} /> : isLoadingScryfall ? <CardShowcaseSkeleton /> : null}

        {/* Add to Deck button (preview mode only) */}
        {!isInDeck && onAddToDeck && (
          <div className="px-6 pb-6">
            <button
              type="button"
              onClick={handleAddToDeck}
              className={clsx(
                'flex w-full items-center justify-center gap-2 rounded-lg px-4 py-2.5 text-sm font-medium transition-colors',
                'bg-aether/90 hover:bg-aether text-white',
              )}
            >
              <IoAddCircleOutline className="h-5 w-5" />
              <span>Add to Deck</span>
            </button>
          </div>
        )}

        {/* In-deck controls - Show skeleton during loading */}
        {mode === 'in-deck' &&
          (deckCard && activeDeck.deck ? (
            <InDeckSection
              deckCardId={deckCard.id}
              deckId={activeDeck.deck.id}
              quantity={deckCard.quantity}
              zone={deckCard.zone}
              format={activeDeck.deck.format}
              status={activeDeck.deck.status}
              card={scryfallCard ?? minimalCard}
              allCards={activeDeck.cards ?? undefined}
              isLocked={activeDeck.deck.is_locked}
            />
          ) : (
            <InDeckSectionSkeleton />
          ))}

        {/* Version selector - only show when deck card is loaded */}
        {mode === 'in-deck' && deckCard && activeDeck.deck && scryfallId && cardName && (
          <VersionSection
            deckCardId={deckCard.id}
            deckId={activeDeck.deck.id}
            scryfallId={scryfallId}
            cardName={cardName}
            isLocked={activeDeck.deck.is_locked}
          />
        )}

        {/* Availability - per-edition inventory vs deck usage */}
        <CardAvailabilitySection
          oracleId={oracleId ?? undefined}
          cardName={cardName !== 'Loading...' ? cardName : undefined}
          selectedScryfallId={scryfallId ?? undefined}
        />

        {/* Prices - Show skeleton during loading */}
        {scryfallCard ? <PricesSection card={scryfallCard} /> : isLoadingScryfall ? <PricesSectionSkeleton /> : null}

        {/* Rulings */}
        {scryfallId && scryfallCard ? <RulingsSection cardId={scryfallId} /> : isLoadingScryfall ? <RulingsSectionSkeleton /> : null}

        {/* Legalities - Reuses collection component */}
        {scryfallCard && <LegalitiesSection card={scryfallCard} />}

        {/* View on Scryfall link */}
        {scryfallCard && (
          <div className="px-6 pb-6">
            <a
              href={`https://scryfall.com/card/${scryfallCard.set}/${scryfallCard.collector_number}`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-silver hover:text-pearl flex items-center justify-center gap-1.5 text-sm transition-colors"
            >
              View on Scryfall
              <IoOpenOutline className="h-4 w-4" />
            </a>
          </div>
        )}
      </div>
    </aside>
  );
}
